import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Box, Paper as MuiPaper, Typography, Chip } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DialogMode } from "../../../../types/dialogmode";
import { IContract, ILogicalCheck } from "../../../../types/contract";
import { IDocument } from "../../../../types/document";
import { useGetHumanDataVerificationsQuery } from "../../../../redux/slices/indexApiSlice";
import { ContractExtractionResult } from "../../../../types/aiOutput";
import VerifiedAISuggestion from "./VerifiedAISuggestion";
import AISuggestion from "./AISuggestion";
import { TreeItem2 } from "@mui/x-tree-view/TreeItem2";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import LogicalCheckAlert from "./LogicalCheckAlert";
import RelevantSectionsSpykio from "./RelevantSectionsSpykio";

const UploadButton = styled(MuiPaper)`
  height: 144px;
  border: 1px dashed ${(props) => props.theme.palette.divider};
  padding: 24px;
  cursor: pointer;
  &:hover {
    border: 1px dashed ${(props) => props.theme.palette.primary.main};
  }
`;

export const NoticePeriodCard = ({
  value,
  unit,
  explanation,
  logicalCheck,
  viewMode,
}: {
  value?: number;
  unit?: string;
  explanation?: string;
  logicalCheck?: ILogicalCheck;
  viewMode?: "compact" | "full";
}) => {
  const { t } = useTranslation();

  return (
    <Grid container size={12} gap={2}>
      {viewMode !== "compact" && logicalCheck && (
        <Grid size={12}>
          <LogicalCheckAlert logicalCheck={logicalCheck} />
        </Grid>
      )}

      {value !== undefined && unit && (
        <Typography variant="body1">
          {value} {t(unit)}
        </Typography>
      )}

      {explanation && viewMode !== "compact" && (
        <Typography variant="body1">{explanation}</Typography>
      )}
    </Grid>
  );
};

interface IContractNoticePeriodProps {
  contract: IContract;
  mode: DialogMode;
  aiOutputs: ContractExtractionResult[];
  hideTitle?: boolean;
  documents: IDocument[];
}
function ContractNoticePeriod({ ...props }: IContractNoticePeriodProps) {
  const { contract, mode, aiOutputs, documents } = props;
  const { t } = useTranslation();

  const { data: verifications } = useGetHumanDataVerificationsQuery(contract);

  return (
    <Box pb={2}>
      {!props.hideTitle && (
        <Typography variant="h6" mb={2}>
          {t("Notice period")}
        </Typography>
      )}

      <NoticePeriodCard {...contract?.noticePeriod} />

      <RelevantSectionsSpykio spykioContext={contract.noticePeriod?.context} />

      {aiOutputs?.length > 0 && (
        <Box my={5}>
          <SimpleTreeView>
            {aiOutputs?.map((aiOutput) => {
              // if (aiOutput.id === latestAiOutput?.id) {
              //   // skip the latest ai output as it is already shown
              //   return null;
              // }

              const currentResult =
                aiOutput.metadata?.structuralData?.noticePeriod;

              if (!currentResult) {
                return null;
              }

              const verificationRecord = verifications?.find(
                (v) =>
                  v.field === "noticePeriod" && v.aiOutputId === aiOutput.id
              );

              const document = documents.find(
                (d) => d.id === aiOutput.documentId
              );

              return (
                <TreeItem2
                  itemId={`${aiOutput.id}-prompts`}
                  label={
                    <Typography>
                      {aiOutput.processedAt
                        ? t("date.dateTime", {
                            date: new Date(aiOutput.processedAt),
                          })
                        : "No processing date"}
                    </Typography>
                  }
                  style={{
                    marginBottom: 5,
                    gap: 5,
                  }}
                  slotProps={{
                    content: {
                      style: {
                        paddingTop: 10,
                        paddingBottom: 10,
                      },
                    },
                  }}
                >
                  <Box my={2} display="flex" flexDirection="column" gap={2}>
                    {document && documents?.length > 1 && (
                      <div>
                        <Chip
                          label={
                            document.type === "attachment"
                              ? document.name
                              : t("Main document")
                          }
                          color="primary"
                          size="small"
                          // icon={<AttachFile />}
                          variant="outlined"
                        />
                      </div>
                    )}

                    {verificationRecord && (
                      <VerifiedAISuggestion
                        verificationRecord={verificationRecord}
                      />
                    )}

                    <AISuggestion
                      contract={contract}
                      mode={mode}
                      isAiSuggestion={!verificationRecord}
                      collapsible={false}
                      isVerified={verificationRecord?.isCorrect !== undefined}
                      field="noticePeriod"
                      value={currentResult}
                      aiInputId={aiOutput.aiInputId}
                      aiOutputId={aiOutput.id}
                      content={<NoticePeriodCard {...currentResult} />}
                      processedAt={aiOutput?.processedAt}
                    />
                  </Box>
                </TreeItem2>
              );
            })}
          </SimpleTreeView>
        </Box>
      )}
    </Box>
  );
}

export default ContractNoticePeriod;
